import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router";
import { useRecoilState } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import useSendEmail from "../hooks/useSendEmail";
import {
  rowForEditScorecard,
  scorecardAddState,
  scorecardEditState,
  scorecardLoading,
  scorecardSaving,
  setLinkStateScoreCard,
} from "../atoms/atoms";
import useAddScoreCard from "../hooks/useAddScorecard";
import useEditScoreCard from "../hooks/useEditScorecard";
import { IScoreCardDetails } from "../types";
import ScoreCardDetails from "./ScoreCardDetails";

interface ScoreCardFormProps {
  isAdd: boolean;
  isEdit: boolean;
  setIsAdd;
  setIsEdit;
  setOnClose;
  searchClicked;
}

export const ScoreCardForm: React.FC<ScoreCardFormProps> = ({
  isAdd,
  isEdit,
  setIsAdd,
  setIsEdit,
  setOnClose,
  searchClicked,
}) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [isLinkedState] = useRecoilState(setLinkStateScoreCard);

  const [isSaving, setIsSaving] = useRecoilState(scorecardSaving);
  const [saveModal, setSaveModal] = useState(false);
  const [isAddState, setAddState] = useRecoilState(scorecardAddState);
  const [, setEditState] = useRecoilState(scorecardEditState);
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditScorecard);
  const [isScorecardLoading, setIsScorecardLoading] =
    useRecoilState(scorecardLoading);
  const { mutateAsync: sendEmail } = useSendEmail();
  const [newData, setNewData] = useState<IScoreCardDetails>();
  const useFormMethods = useForm<IScoreCardDetails>({
    defaultValues: { ...rowForEdit },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;

  const handleLink = () => {
    history.push({
      pathname: "/carrieragreementmgmt/scorecard",
    });
    window.location.reload();
  };

  // Add Scorecard Initialization
  const {
    mutate: addScoreCard,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddScoreCard();

  //Edit Scorecard Initialization/
  const {
    mutate: editScoreCard,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditScoreCard();

  const handleSave = () => {
    setSaveModal(false);
    if (isAddState) {
      addScoreCard(newData);
      if (isLinkedState) {
        setTimeout(() => handleLink(), 5000);
      }
    } else {
      editScoreCard(newData);
    }
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setIsAdd(false);
    setIsEdit(false);
    setOnClose(true);
  };

  if (addSuccess && addResult) {
    if (addResult.status === 501) {
      toast.fire("Failed", "Scorecard Record Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      sendEmail(addResult.data, {
        onSuccess: (res) => {
          if (res) {
            if (addResult.status === 400) {
              toast.fire(
                "Success",
                "Scorecard Added Successfully, but failed in sending email",
                "warning"
              );
            } else {
              toast.fire("Success", "Scorecard Added Successfully", "success");
            }
          }
        },
      });

      queryClient.invalidateQueries("GetScoreCard");
      queryClient.invalidateQueries("GetScoreCardsList");
      searchClicked();
      handleCancel();
      setAddState(false);
      setIsAdd(false);
      toast.fire("Success", "Scorecard Added Successfully", "success");
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Scorecard added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire("Failed", "Failed to Add Scorecard! No Data Saved", "error");
      }
      queryClient.invalidateQueries("GetScoreCard");
      queryClient.invalidateQueries("GetScoreCardsList");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Scorecard! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetScoreCard");
      queryClient.invalidateQueries("GetScoreCardsList");
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Scorecard! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetScoreCard");
    queryClient.invalidateQueries("GetScoreCardsList");
    setAddState(false);
  }

  //Edit result
  if (editSuccess && editResult) {
    if (editResult && editResult.status === 501) {
      toast.fire("Failed", "Scorecard Record Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (
      (editResult && editResult.status === 201) ||
      editResult.status === 200
    ) {
      sendEmail(editResult.data, {
        onSuccess: (res) => {
          if (res) {
            if (editResult.status === 400) {
              toast.fire(
                "Success",
                "Scorecard Updated Successfully, but failed in sending email",
                "warning"
              );
            } else {
              toast.fire(
                "Success",
                "Scorecard Updated Successfully",
                "success"
              );
            }
          }
        },
      });
      queryClient.invalidateQueries("GetScoreCard");
      queryClient.invalidateQueries("GetScoreCardsList");
      searchClicked();
      handleCancel();
      setRowForEdit(undefined);
      setEditState(false);
      setIsEdit(false);
      toast.fire("Success", "Scorecard Updated Successfully", "success");
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Scorecard updated successfully, but " + editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setIsSaving(false);
        setRowForEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to Update Scorecard! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetScoreCard");
      queryClient.invalidateQueries("GetScoreCardsList");
      setEditState(false);
      setIsEdit(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Update Scorecard! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetScoreCard");
      queryClient.invalidateQueries("GetScoreCardsList");
      setEditState(false);
      setIsEdit(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setSaveModal(false);
    setIsEdit(false);
    toast.fire(
      "Server Error",
      "Failed to Update Scorecard! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetScoreCard");
    queryClient.invalidateQueries("GetScoreCardsList");
    setEditState(false);
    setIsEdit(false);
  }

  const onSubmit = (data: IScoreCardDetails) => {
    setNewData({
      ...data,
      createdDate: data.createdDate
        ? new Date(data.createdDate).toISOString()
        : null,
      agentUnknownC: !data.agentUnknownC ? false : data.agentUnknownC,
      isEscalated: data.isEscalated ? data.isEscalated : false,
      baseUrl: window.location.origin,
      fullPayC:
        data.fullPay === null ? null : data.fullPay === "Yes" ? true : false,
      disputeId: data.disputeId === null ? 0 : data.disputeId,
    });

    if (isAdd) {
      setSaveModal(true);
    } else if (isEdit && isDirty) {
      setSaveModal(true);
    } else {
      setIsSaving(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };
  return (
    <>
      <div className="p-3 space-y-2 border-l-2 border-primary box-shadow mt-2">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
              <div className="ml-2 font-bold text-primary text-md">
                {isAdd ? "Add Scorecard" : "Edit Scorecard"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={isSaving}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={() => {
                    if (isLinkedState) {
                      handleLink();
                    }
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 border-secondary mr-2 ml-2"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
            <div className="">
              <ScoreCardDetails rowForEdit={rowForEdit} />
            </div>
          </form>
        </FormProvider>
      </div>
      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Scorecard" : "Edit Scorecard"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "Add " : "Update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
